import { combineReducers } from 'redux';
import omit from 'lodash/omit';
import update from 'immutability-helper';
import { initialPagination, parsePagination } from 'redux/helpers/pagination';
import types from './types';

const initialFeed = {
  items: [],
  pagination: initialPagination,
  loading: false,
  filter: null,
};

const mergeItem = (items, updated) => {
  if (!updated?.id) {
    return items;
  }

  return items.map((item) => (item.id === updated.id ? { ...item, ...updated } : item));
};

const feed = (state = initialFeed, { type, payload }) => {
  switch (type) {
    case types.getNetworkFeed:
      return payload.page <= 1
        ? { ...initialFeed, loading: true, filter: payload.filter }
        : { ...state, loading: true };

    case types.getNetworkFeedSuccess: {
      if (payload.request.filter !== state.filter) {
        return { ...state, loading: false };
      }

      // Merged, not appended: removing a card shifts the offsets a refetch reads from.
      const known = new Set(state.items.map((item) => item.id));
      const fresh = (payload.data ?? []).filter((item) => !known.has(item.id));

      return {
        ...state,
        loading: false,
        items: [...state.items, ...fresh],
        pagination: parsePagination(payload),
      };
    }

    case types.getNetworkFeedFailure:
      return update(state, { loading: { $set: false }, pagination: { hasMore: { $set: false } } });

    case types.removeNetworkFeedItem:
      return { ...state, items: state.items.filter((item) => item.id !== payload.id) };

    case types.resolveNetworkShareSuccess:
    case types.publishNetworkShareSuccess:
      return { ...state, items: mergeItem(state.items, payload.data) };

    case types.withdrawNetworkShareSuccess: {
      const { shareId } = payload.request;
      const change = payload.data?.removed
        ? { withdrawn: { $set: true } }
        : { recipients: { pending: { $set: 0 } } };

      return {
        ...state,
        items: state.items.map((item) => (item.id === shareId ? update(item, change) : item)),
      };
    }

    default:
      return state;
  }
};

const initialSettings = {
  items: [],
  loading: true,
};

const settings = (state = initialSettings, { type, payload }) => {
  switch (type) {
    case types.getNetworkSettings:
      return { ...state, loading: true };

    case types.getNetworkSettingsSuccess:
      return { items: payload.data ?? [], loading: false };

    case types.getNetworkSettingsFailure:
      return { ...state, loading: false };

    case types.updateNetworkSettingsSuccess: {
      const updated = payload.data;

      if (!updated?.network?.id) {
        return state;
      }

      return {
        ...state,
        items: state.items.map((item) => (item.network.id === updated.network.id ? updated : item)),
      };
    }

    default:
      return state;
  }
};

const autoPostCandidates = (state = { ids: [], loading: true }, { type, payload }) => {
  switch (type) {
    case types.getNetworkAutoPostCandidates:
      return { ...state, loading: true };

    case types.getNetworkAutoPostCandidatesSuccess:
      return { ids: payload.data ?? [], loading: false };

    case types.getNetworkAutoPostCandidatesFailure:
      return { ...state, loading: false };

    default:
      return state;
  }
};

// commonApiSaga forwards no config to apiCall, so an onComplete callback never fires.
const loadingByFeedItem = (state = {}, { type, payload }) => {
  switch (type) {
    case types.resolveNetworkShare:
    case types.publishNetworkShare:
      return { ...state, [payload.targetId]: true };

    case types.withdrawNetworkShare:
      return { ...state, [payload.shareId]: true };

    case types.resolveNetworkShareSuccess:
    case types.publishNetworkShareSuccess:
    case types.resolveNetworkShareFailure:
    case types.publishNetworkShareFailure:
      return omit(state, payload?.request?.targetId);

    case types.withdrawNetworkShareSuccess:
    case types.withdrawNetworkShareFailure:
      return omit(state, payload?.request?.shareId);

    default:
      return state;
  }
};

const savingByNetwork = (state = {}, { type, payload }) => {
  switch (type) {
    case types.updateNetworkSettings:
      return { ...state, [payload.networkId]: true };

    case types.updateNetworkSettingsSuccess:
    case types.updateNetworkSettingsFailure:
      return omit(state, payload?.request?.networkId);

    default:
      return state;
  }
};

const failedByNetwork = (state = {}, { type, payload }) => {
  switch (type) {
    case types.updateNetworkSettings:
    case types.updateNetworkSettingsSuccess:
      return omit(state, payload?.request?.networkId ?? payload?.networkId);

    case types.updateNetworkSettingsFailure:
      return { ...state, [payload?.request?.networkId]: true };

    default:
      return state;
  }
};

const network = combineReducers({
  feed,
  settings,
  autoPostCandidates,
  loadingByFeedItem,
  savingByNetwork,
  failedByNetwork,
});

export default { network };
